import { Link } from '@tanstack/react-router'
import { ExternalLink, Wallet } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

import type { CashFlowTrend } from '@/hooks'

interface CashFlowChartWidgetProps {
  cashFlow: CashFlowTrend[] | null
  isLoading?: boolean
}

function formatCurrency(cents: number) {
  return `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 0 })}`
}

function formatMonth(month: string) {
  const [, m] = month.split('-')
  return `${parseInt(m)}月`
}

interface TooltipPayload {
  value: number
  name: string
  color: string
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: TooltipPayload[]
  label?: string
}) {
  if (!active || !payload || !label) {return null}

  return (
    <div className="bg-background border rounded-lg shadow-lg p-3">
      <p className="font-medium mb-2">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color }} className="text-sm">
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  )
}

export function CashFlowChartWidget({ cashFlow, isLoading }: CashFlowChartWidgetProps) {
  const chartData = cashFlow?.map((item) => ({
    month: formatMonth(item.month),
    收入: item.income,
    支出: item.expense,
    净额: item.net,
  }))

  return (
    <div className="rounded-lg border bg-card col-span-full lg:col-span-2">
      <div className="flex items-center justify-between p-6 pb-4">
        <h3 className="flex items-center gap-2 font-semibold">
          <Wallet className="h-5 w-5" />
          现金流趋势
        </h3>
        {/* @ts-expect-error - TanStack Router type inference issue with literal paths */}
        <Link
          to="/finance/cash-flows"
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          查看详情 <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      <div className="px-6 pb-6">
        {isLoading ? (
          <div className="h-[300px] w-full bg-muted rounded animate-pulse" />
        ) : !cashFlow || cashFlow.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            暂无现金流数据
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" className="text-xs" />
              <YAxis
                className="text-xs"
                tickFormatter={(value: number) => formatCurrency(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="收入"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ fill: '#22c55e' }}
              />
              <Line
                type="monotone"
                dataKey="支出"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: '#ef4444' }}
              />
              <Line
                type="monotone"
                dataKey="净额"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#3b82f6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
